import { Checkbox, Input, NothingToShow } from 'shared-ui';

import { DOD_QUEUE_SORTS, DodQueueSort } from 'api/dod/dod.v2.types';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DenseTable } from '../components/denseTable/DenseTable';
import { DenseSort, DenseTableColumn } from '../components/denseTable/DenseTable.types';
import { StateChip } from '../components/stateChip/StateChip';
import { dodReasonLabel, formatDurationSeconds, relativeSecondsSince } from '../overviewTab/dodFormat';

import * as Styled from './QueuesTab.styles';
import { QueueTableRow, QueuesTabProps, queueRowId } from './QueuesTab.types';

const isQueueSort = (key: string): key is DodQueueSort => DOD_QUEUE_SORTS.includes(key as DodQueueSort);

/**
 * The fleet's TKD queues in the application's table.
 *
 * Every control is server-side: sorting, the toolbar filters, the debounced search box, the
 * "backlog only" checkbox (`minDepth=1`) and the pagination are all query parameters, so a header
 * click only reports the requested sort and nothing is reordered here. The RCP server is a column
 * rather than a section row, which keeps one row height for the whole table.
 *
 * Rows carry no buttons: like the card and event lists, a row opens the details panel and the
 * queue's actions (drain, pause, resume) live there. Column minimums add up to well under the
 * narrowest content width the app supports, so the table never pushes the page wider than the
 * viewport.
 */
export const QueuesTab = ({
  rows,
  totals,
  isLoading,
  isError,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sort,
  order,
  onSortChange,
  search,
  onSearchChange,
  isBacklogOnly,
  onBacklogOnlyToggle,
  selected,
  onSelect,
}: QueuesTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const unknownLabel = formatMessage({ id: AppMessages['dod.overview.unknown'] });
  const searchLabel = formatMessage({ id: AppMessages['dod.queues.search'] });

  const renderRelative = (iso?: string): string => {
    const seconds = relativeSecondsSince(iso);

    if (seconds === null) {
      return '—';
    }

    return formatMessage(
      { id: AppMessages['dod.queues.relativeAgo'] },
      { time: formatDurationSeconds(seconds, unknownLabel) },
    );
  };

  const columns: Array<DenseTableColumn<QueueTableRow>> = [
    {
      key: 'rcp',
      header: formatMessage({ id: AppMessages['dod.queues.column.rcp'] }),
      width: 'minmax(90px, 1fr)',
      render: (row) => row.rcpNazwa ?? row.rcpId,
    },
    {
      key: 'tkd',
      header: formatMessage({ id: AppMessages['dod.queues.column.tkd'] }),
      width: 'minmax(130px, 1.4fr)',
      render: (row) =>
        `${row.tkdNazwa ?? formatMessage({ id: AppMessages['dod.queues.tkd.unnamed'] })} · NLK ${row.nlk}`,
    },
    {
      key: 'state',
      header: formatMessage({ id: AppMessages['dod.queues.column.state'] }),
      width: '140px',
      render: (row) => <StateChip state={row.status} tooltip={row.status === 'paused' ? row.lastError : undefined} />,
    },
    {
      key: 'depth',
      header: formatMessage({ id: AppMessages['dod.queues.column.depth'] }),
      width: '100px',
      sortable: true,
      render: (row) => row.depth,
    },
    {
      key: 'dlq',
      header: formatMessage({ id: AppMessages['dod.queues.column.dlq'] }),
      width: '90px',
      sortable: true,
      render: (row) => row.dlqDepth,
    },
    {
      key: 'lastDrainAt',
      header: formatMessage({ id: AppMessages['dod.queues.column.lastDrain'] }),
      width: '120px',
      sortable: true,
      render: (row) => renderRelative(row.lastDrainAt),
    },
    {
      key: 'retries',
      header: formatMessage({ id: AppMessages['dod.queues.column.retries'] }),
      width: '95px',
      render: (row) => row.retryCountMax,
    },
    {
      key: 'lastError',
      // A plain string goes through Table.RowItem, which ellipsises it and tooltips the full text.
      header: formatMessage({ id: AppMessages['dod.queues.column.lastError'] }),
      width: 'minmax(130px, 1.2fr)',
      render: (row) => dodReasonLabel(row.lastError, formatMessage) ?? '—',
    },
  ];

  const onDenseSortChange = ({ key, direction }: DenseSort): void => {
    if (isQueueSort(key)) {
      onSortChange(key, direction);
    }
  };

  const onClickRow = (row: QueueTableRow): void => {
    const isSelected = selected?.rcpId === row.rcpId && selected?.nlk === row.nlk;

    onSelect(isSelected ? null : { rcpId: row.rcpId, nlk: row.nlk });
  };

  if (isError) {
    return (
      <div role="alert">
        <NothingToShow text={formatMessage({ id: AppMessages['dod.queues.error'] })} />
      </div>
    );
  }

  return (
    <Styled.Layout>
      <Styled.Toolbar>
        <Styled.SearchField>
          <Input
            placeholder={searchLabel}
            value={search}
            withoutHelperText
            onChange={(event) => onSearchChange(event.target.value)}
            inputProps={{ 'aria-label': searchLabel }}
          />
        </Styled.SearchField>

        <Styled.CheckboxLabel>
          <Checkbox
            color="primary"
            checked={isBacklogOnly}
            onChange={onBacklogOnlyToggle}
            inputProps={{ 'aria-label': formatMessage({ id: AppMessages['dod.queues.backlogOnly'] }) }}
          />
          {formatMessage({ id: AppMessages['dod.queues.backlogOnly'] })}
        </Styled.CheckboxLabel>
      </Styled.Toolbar>

      <Styled.Summary>
        {formatMessage(
          { id: AppMessages['dod.queues.summary'] },
          { queues: totals.queues, depth: totals.depth, dlq: totals.dlq, total },
        )}
      </Styled.Summary>

      <DenseTable<QueueTableRow>
        columns={columns}
        data={rows}
        sort={{ key: sort, direction: order }}
        onSortChange={onDenseSortChange}
        isLoading={isLoading}
        nothingToShowText={formatMessage({ id: AppMessages['dod.queues.empty'] })}
        onClickRow={onClickRow}
        activeRowId={selected ? queueRowId(selected) : undefined}
        pagination={{
          // The shared pagination counts rows and numbers pages from zero; the API does neither.
          page: page - 1,
          count: total,
          rowsPerPage: pageSize,
          setPage: (nextPage) => onPageChange(nextPage + 1),
          setRowsPerPage: onPageSizeChange,
        }}
      />
    </Styled.Layout>
  );
};
